import { useState, useEffect } from "react"
import { ArrowLeft, ArrowRight } from "lucide-react"
import { toast } from "sonner"
import { useLocation, useParams, useNavigate } from "react-router-dom"
import { useAppSelector } from "../app/hooks"
import { ProfileHeader } from "../components/Candidate-Sections/profile-header"
import { ProfileOverview } from "../components/Candidate-Sections/profile-overview"
import { ProfileFeedback } from "../components/Candidate-Sections/profile-feedback"
import { AptitudeTest } from "../components/Candidate-Sections/aptitude-test"
import { fetchCandidateDetail, fetchJobMatches, formatCandidateCurrentLocation, type CandidateDetail, type JobMatch } from "../features/jobs/jobAPI"

export default function CandidatePage() {
    const location = useLocation()
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const accessToken = useAppSelector((state) => state.auth.accessToken)
    const userGroups = useAppSelector((state) => state.auth.user?.groups ?? [])
    
    const [activeTab, setActiveTab] = useState("overview")
    const [candidateData, setCandidateData] = useState<CandidateDetail | null>(location.state?.candidateData || null)
    const [matches, setMatches] = useState<JobMatch[]>(location.state?.matches || [])
    const [jobId] = useState<number | null>(location.state?.jobId || null)
    const [isLoading, setIsLoading] = useState(!location.state?.candidateData)
    const [error, setError] = useState<string | null>(null)
    const [feedbackCount, setFeedbackCount] = useState(0)

    // Get current candidate ID
    const currentCandidateId = id ? Number(id) : null

    // Find current candidate index in matches
    const currentMatchIndex = matches.findIndex(match => match.candidate.id === currentCandidateId)
    
    // Find current match based on candidate ID
    const currentMatch = matches.find(match => match.candidate.id === currentCandidateId)
    const matchId = currentMatch?.id || null
    const canAccessFeedbackRole =
        userGroups.includes("company_hiring_manager") ||
        userGroups.includes("company_recruiter")
    const normalizedStage = (currentMatch?.stage ?? "").trim()
    const canShowFeedback =
        canAccessFeedbackRole &&
        (normalizedStage === "In review" ||
            normalizedStage === "Shortlisted" ||
            normalizedStage === "Verdict")

    useEffect(() => {
        if (activeTab === "feedback" && !canShowFeedback) {
            setActiveTab("overview")
        }
    }, [activeTab, canShowFeedback])

    // Fetch matches if not provided in location state
    useEffect(() => {
        if (!accessToken || !jobId) return

        // Only fetch matches if not provided in location state
        if (!location.state?.matches && jobId) {
            const loadMatches = async () => {
                try {
                    const response = await fetchJobMatches(jobId, accessToken)
                    if (response.error) {
                        console.error("Failed to fetch matches:", response.error)
                    } else if (response.data) {
                        setMatches(response.data)
                    }
                } catch (err) {
                    console.error("Error fetching matches:", err)
                }
            }

            loadMatches()
        }
    }, [jobId, accessToken, location.state])

    // Fetch candidate data if not provided in location state
    useEffect(() => {
        if (!accessToken || !currentCandidateId) return

        // Only fetch if data wasn't passed via location state
        if (!location.state?.candidateData) {
            const loadCandidate = async () => {
                try {
                    setIsLoading(true)
                    setError(null)

                    const response = await fetchCandidateDetail(currentCandidateId, accessToken)
                    if (response.error) {
                        setError(response.error)
                    } else if (response.data) {
                        setCandidateData(response.data)
                    }
                } catch (err) {
                    const message = err instanceof Error ? err.message : "Failed to load candidate details."
                    setError(message)
                } finally {
                    setIsLoading(false)
                }
            }

            loadCandidate()
        }
    }, [currentCandidateId, accessToken, location.state])

    const transformCandidateData = (data: CandidateDetail): any => {
        return {
            id: data.id,
            name: data.full_name,
            role: data.job_title || "N/A",
            experience: data.years_of_professional_experience?.name || "N/A",
            location: formatCandidateCurrentLocation(data.current_location),
            profile_picture: data.profile_picture, // Preserve original profile_picture (can be number or null)
            avatar: data.profile_picture || "/placeholder.svg", // Keep for backward compatibility
            matchedSkills: 0, // Will need to calculate from match data
            missingSkills: 0, // Will need to calculate from match data
            score: currentMatch?.overall_score ? parseFloat(currentMatch.overall_score) : 0,
            expertise: data.areas_of_expertise?.map((area) => ({
                category: area.field_of_expertise?.name || "N/A",
                years: area.years_in_this_area_of_expertise || 0,
                healthcare: area.company?.map((c) => c.name).join(", ") || "N/A",
                locations: area.region?.map((r) => r.name).join(", ") || "N/A",
                mustHave: area.key_skills?.map((skill) => ({ name: skill.name, variant: "solid" as const })) || [],
                niceToHave: [], // API doesn't provide nice-to-have skills separately
            })) || [],
            aptitude: {
                percentile: 0,
                rawScore: 0,
                tests: [],
            },
            gender: data.gender === "M" ? "Male" : data.gender === "F" ? "Female" : "Other",
            dateOfBirth: data.date_of_birth ? new Date(data.date_of_birth).toLocaleDateString() : "N/A",
            email: data.email?.trim() || "",
            contactNumber: data.phone_number?.trim() || "N/A",
            visaStatus: data.preferred_locations?.[0]?.visa_status || "N/A",
            drivingLicense: data.preferred_locations?.[0]?.driving_license || "N/A",
            nationality: data.nationality?.name || "N/A",
            languages: data.language_skills?.map((lang) => ({
                lang: lang.language?.name || "N/A",
                level: lang.speaking_level || "N/A",
                skills: lang.reading_writing_level || "N/A",
            })) || [],
            education: data.education?.map((edu) => ({
                degree: `${edu.qualification?.name || ""} in ${edu.field_of_study?.name || ""}`.trim() || "N/A",
                university: edu.institution?.name || "N/A",
                years: edu.year_of_obtension?.toString() || "N/A",
            })) || [],
            certifications: [], // Not in API response
            teamExperience: data.manages_people ? "Yes" : "No",
        }
    }

    // Use candidateData if available
    const currentCandidate = candidateData ? transformCandidateData(candidateData) : null

    const handleNext = async () => {
        if (matches.length === 0 || currentMatchIndex === -1) return

        // Get next candidate index (wrap around if at the end)
        const nextIndex = (currentMatchIndex + 1) % matches.length
        const nextMatch = matches[nextIndex]
        const nextCandidateId = nextMatch.candidate.id

        if (!accessToken) return

        try {
            setIsLoading(true)
            setError(null)

            // Fetch next candidate details
            const response = await fetchCandidateDetail(nextCandidateId, accessToken)
            if (response.error) {
                toast.error(response.error || "Failed to load next candidate")
                // Navigate back to job matches page
                if (jobId) {
                    navigate("/job-matches", { state: { jobId: jobId } })
                } else {
                    navigate("/job-matches")
                }
            } else if (response.data) {
                setCandidateData(response.data)
                // Navigate to next candidate with matches and jobId in state
                navigate(`/candidate/${nextCandidateId}`, {
                    state: {
                        candidateData: response.data,
                        matches: matches,
                        jobId: jobId,
                    },
                    replace: true,
                })
            }
        } catch (err) {
            const message = err instanceof Error ? err.message : "Failed to load next candidate."
            toast.error(message)
            // Navigate back to job matches page
            if (jobId) {
                navigate("/job-matches", { state: { jobId: jobId } })
            } else {
                navigate("/job-matches")
            }
        } finally {
            setIsLoading(false)
        }
    }

    const handlePrev = async () => {
        if (matches.length === 0 || currentMatchIndex === -1) return

        // Get previous candidate index (wrap around if at the beginning)
        const prevIndex = (currentMatchIndex - 1 + matches.length) % matches.length
        const prevMatch = matches[prevIndex]
        const prevCandidateId = prevMatch.candidate.id

        if (!accessToken) return

        try {
            setIsLoading(true)
            setError(null)

            // Fetch previous candidate details
            const response = await fetchCandidateDetail(prevCandidateId, accessToken)
            if (response.error) {
                toast.error(response.error || "Failed to load previous candidate")
                // Navigate back to job matches page
                if (jobId) {
                    navigate("/job-matches", { state: { jobId: jobId } })
                } else {
                    navigate("/job-matches")
                }
            } else if (response.data) {
                setCandidateData(response.data)
                // Navigate to previous candidate with matches and jobId in state
                navigate(`/candidate/${prevCandidateId}`, {
                    state: {
                        candidateData: response.data,
                        matches: matches,
                        jobId: jobId,
                    },
                    replace: true,
                })
            }
        } catch (err) {
            const message = err instanceof Error ? err.message : "Failed to load previous candidate."
            toast.error(message)
            // Navigate back to job matches page
            if (jobId) {
                navigate("/job-matches", { state: { jobId: jobId } })
            } else {
                navigate("/job-matches")
            }
        } finally {
            setIsLoading(false)
        }
    }

    const handleClose = () => {
        // Navigate back to matches page with jobId
        if (jobId) {
            navigate("/job-matches", { state: { jobId: jobId } })
        } else {
            navigate("/job-matches")
        }
    }

    if (isLoading) {
        return (
            <div className="min-h-screen bg-[#F8F9FA] p-4 md:p-8 font-sans flex items-center justify-center">
                <p className="text-sm text-gray-500">Loading candidate details...</p>
            </div>
        )
    }

    if (error) {
        return (
            <div className="min-h-screen bg-[#F8F9FA] p-4 md:p-8 font-sans flex items-center justify-center">
                <p className="text-sm text-gray-500">Candidate not found.</p>
            </div>
        )
    }

    if (!currentCandidate) {
        return (
            <div className="min-h-screen bg-[#F8F9FA] p-4 md:p-8 font-sans flex items-center justify-center">
                <p className="text-sm text-gray-500">Candidate not found.</p>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-[#F8F9FA] p-4 md:p-8 font-sans">
            <div className="mx-auto max-w-6xl">
                {/* Navigation Header */}
                {matches.length > 0 && currentMatchIndex !== -1 && (
                    <div className="mb-6 flex items-center justify-between px-2">
                        <button
                            onClick={handlePrev}
                            disabled={isLoading}
                            className="flex items-center gap-1 text-sm font-medium text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <div className="bg-[#EEF4FE] p-1 rounded-md">
                                <ArrowLeft className="h-4 w-4" />
                            </div>
                            Previous
                        </button>
                        <button
                            onClick={handleNext}
                            disabled={isLoading}
                            className="flex items-center gap-1 text-sm font-medium text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Next
                            <div className="bg-[#EEF4FE] p-1 rounded-md">
                                <ArrowRight className="h-4 w-4" />
                            </div>
                        </button>
                    </div>
                )}

                <ProfileHeader
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    candidate={currentCandidate}
                    jobId={jobId}
                    matchId={matchId}
                    stage={currentMatch?.stage ?? null}
                    status={currentMatch?.recruiter_action ?? null}
                />

                {activeTab === "overview" && (
                    <ProfileOverview
                        candidate={currentCandidate}
                        candidateDetail={candidateData}
                        onClose={handleClose}
                        jobId={jobId}
                        matchId={matchId}
                        stage={currentMatch?.stage ?? null}
                        hasFeedback={feedbackCount > 0}
                        interviewDate={currentMatch?.recruiter_action_date ?? null}
                        onInterviewDateSaved={(dateStr) => {
                            setMatches((prev) =>
                                prev.map((m) =>
                                    m.id === currentMatch?.id ? { ...m, recruiter_action_date: dateStr } : m
                                )
                            )
                        }}
                    />
                )}
                {activeTab === "feedback" && canShowFeedback && (
                    <ProfileFeedback 
                        jobId={jobId} 
                        candidateId={currentCandidateId}
                        onFeedbackCountChange={setFeedbackCount}
                    />
                )}
                {activeTab === "aptitude" && <AptitudeTest candidate={currentCandidate} />}

            </div>
        </div>
    )
}
